import React, { useEffect, useRef, useState } from "react";

// Styles
import GeneratedNotesDashboardContainer from "./GeneratedNotesDashboard.styles";

// Font Awesome
import {
    faArrowLeft,
    faCalendar,
    faCheck,
    faRefresh,
} from "@fortawesome/free-solid-svg-icons";

// Utils
import * as Database from "utils/database.utils";

// Components
import LoadingSpinner from "components/Common/LoadingSpinner/LoadingSpinner";
import MorningMeetingCompanyNotes from "../MorningMeetingCompanyNotes/MorningMeetingCompanyNotes";
import { useSettings } from "context/Settings.context";
// React Router
import { useHistory, useLocation, useParams } from "react-router-dom";

// Context
import { MorningMeeting } from "../MorningMeetings/MorningMeetings.types";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toDateAndTimeSeconds } from "../../../utils/general.utils";
import Button from "../../../components/Common/Button/Button";
import { Note, PageParams } from "./GeneratedNotesDashboard.types";

const REGENERATION_POLL_INTERVAL_MS = 10000; // 10 seconds
const REGENERATION_POLL_TIMEOUT_MS = 900000; // 15 minutes

const GeneratedNotesDashboard = () => {
    // @ts-ignore
    const { meetingId, notesType } = useParams<PageParams>();
    const history = useHistory();
    const location = useLocation();

    const [loading, setLoading] = useState(false);
    const [reload, setReload] = useState(false);
    const [notes, setNotes] = useState<Note[]>([]);
    const [meetingDetails, setMeetingDetails] = useState<MorningMeeting | null>(
        null
    );
    const regenerationPollIntervalRef = useRef<ReturnType<
        typeof setInterval
    > | null>(null);

    const { setShowLoadingToast, setToast } = useSettings();

    const getMorningMeetingNotes = async () => {
        const confirmedParam = notesType === "confirmed";
        const response = await Database.get(
            `api/bqmq_voting/morning-meetings/${meetingId}/notes?confirmed=${confirmedParam}`
        );
        if (response.ok) {
            const data = await response.json();
            setNotes(data.data);
        }
    };

    const getMorningMeetingDetails = async (meeting_id: string) => {
        const response = await Database.get(
            `api/bqmq_voting/morning-meetings/${meeting_id}`
        );
        if (response.ok) {
            const data = await response.json();
            setMeetingDetails(data.data);
        }
    };

    const confirmNotes = async () => {
        setShowLoadingToast({
            show: true,
            message: "APPROVING",
        });

        const payload = {
            note_ids: notes
                .filter((n) => n.deleted === 0 && n.archived === 0)
                .map((n) => n.id)
                .filter((id) => !!id),
        };

        const response = await Database.post(
            `api/bqmq_voting/morning-meetings/${meetingId}/notes/confirm`,
            payload
        );
        const data = await response.json();

        setShowLoadingToast({
            show: false,
            message: "APPROVING",
        });

        if (response.ok) {
            setToast({
                show: true,
                message: data?.data?.message,
                type: "success",
            });
            goBack();
        } else {
            setToast({
                show: true,
                message: data?.data?.message,
                type: "error",
            });
            setReload(true);
        }
    };

    const regenerateNotes = async () => {
        const response = await Database.post(
            `api/bqmq_voting/morning-meetings/${meetingId}/regenerate`,
            {}
        );
        const data = await response.json();

        if (response.ok) {
            setToast({
                show: true,
                type: "success",
                message: "Notes are being regenerated",
            });
            // Refetch meeting details and notes list so is_generating_notes is true and polling starts
            await Promise.all([
                getMorningMeetingNotes(),
                getMorningMeetingDetails(meetingId),
            ]);
        } else {
            setToast({
                show: true,
                message: data?.message,
                type: "error",
            });
        }
    };

    const goBack = () => {
        history.push(`/portfolio/morning-meetings/${meetingId}`);
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            await Promise.all([
                getMorningMeetingNotes(),
                getMorningMeetingDetails(meetingId),
            ]);

            setLoading(false);
        };

        fetchData();
    }, [meetingId, notesType]);

    useEffect(() => {
        const reloadData = async () => {
            setLoading(true);
            await getMorningMeetingNotes();
            setLoading(false);
            setReload(false);
        };

        if (reload) {
            reloadData();
        }
    }, [reload]);

    // Effect for stopping polling when regeneration finishes
    useEffect(() => {
        if (
            meetingDetails &&
            !meetingDetails.is_generating_notes &&
            regenerationPollIntervalRef.current
        ) {
            clearInterval(regenerationPollIntervalRef.current);
            regenerationPollIntervalRef.current = null;
            getMorningMeetingNotes();
        }
    }, [meetingDetails?.is_generating_notes]);

    // Effect for starting polling meeting details while regeneration is in progress
    useEffect(() => {
        if (
            notesType !== "unconfirmed" ||
            !meetingDetails?.is_generating_notes ||
            regenerationPollIntervalRef.current
        ) {
            return;
        }

        const id = setInterval(() => {
            getMorningMeetingDetails(meetingId);
            getMorningMeetingNotes();
        }, REGENERATION_POLL_INTERVAL_MS);
        regenerationPollIntervalRef.current = id;

        const timeoutId = setTimeout(() => {
            if (regenerationPollIntervalRef.current === id) {
                clearInterval(id);
                regenerationPollIntervalRef.current = null;
            }
        }, REGENERATION_POLL_TIMEOUT_MS);

        return () => {
            clearInterval(id);
            clearTimeout(timeoutId);
            if (regenerationPollIntervalRef.current === id) {
                regenerationPollIntervalRef.current = null;
            }
        };
    }, [meetingId, notesType, meetingDetails?.is_generating_notes]);

    if (loading)
        return (
            <GeneratedNotesDashboardContainer className="pageContainer">
                <div className={"loadingScreen"}>
                    <LoadingSpinner />
                </div>
            </GeneratedNotesDashboardContainer>
        );

    return (
        <GeneratedNotesDashboardContainer className="pageContainer">
            {meetingDetails?.is_generating_notes === true && (
                <Alert
                    severity="info"
                    sx={{
                        marginTop: 2,
                        marginBottom: 2,
                        cursor: "default",
                    }}
                >
                    Notes are being regenerated. This may take a few minutes.
                    The page will update automatically when ready.
                </Alert>
            )}
            <div className="title-container">
                <div className="left-container">
                    <h1 className="page-title">{meetingDetails?.title}</h1>
                </div>
                <div className="right-container">
                    {notesType === "unconfirmed" && (
                        <>
                            <Button
                                text={
                                    meetingDetails?.is_generating_notes === true
                                        ? "REGENERATING..."
                                        : "REGENERATE NOTES"
                                }
                                click={regenerateNotes}
                                icon={faRefresh}
                                disabled={
                                    meetingDetails?.is_generating_notes === true
                                }
                                size="medium"
                            />
                            {notes && notes.length > 0 && (
                                <Button
                                    text="APPROVE NOTES"
                                    click={confirmNotes}
                                    icon={faCheck}
                                    disabled={
                                        meetingDetails?.is_generating_notes ===
                                        true
                                    }
                                    size="medium"
                                />
                            )}
                        </>
                    )}
                    <Button
                        text="BACK"
                        click={goBack}
                        icon={faArrowLeft}
                        size="medium"
                    />
                </div>
            </div>
            <div className="details">
                <Tooltip title={<h2>MEETING DATE</h2>}>
                    <div className={"detail"}>
                        <FontAwesomeIcon icon={faCalendar} fixedWidth />
                        <p className={"capitalize"}>
                            {toDateAndTimeSeconds(meetingDetails?.date)}
                        </p>
                    </div>
                </Tooltip>
            </div>
            <hr />
            {notes ? (
                <div className={"morning-notes"}>
                    <MorningMeetingCompanyNotes
                        notes={notes}
                        setNotes={setNotes}
                        backPath={location.pathname}
                        backLabel="Back to generated notes"
                        areNotesEditable={notesType === "unconfirmed"}
                    />
                </div>
            ) : (
                <div className="loadingScreen">
                    <h3 style={{ marginTop: "20px" }}>
                        No morning meetings found for {meetingDetails?.title}
                    </h3>
                </div>
            )}
        </GeneratedNotesDashboardContainer>
    );
};

export default GeneratedNotesDashboard;
